import { toCompanyTag, type CompanyTagFields } from "@/lib/company-tag";
import {
  companyPreviewPanelStore,
  type CompanyPreviewPanelEntry,
  type CompanyPreviewPanelSurface,
} from "@/stores/company-preview-panel-store";
import type { CompanyTag } from "@shaarp/common";

/** The tag a chip carries in its `data-company-tag` attribute, or null when unreadable. */
function tagOfChip(chip: HTMLElement): CompanyTag | null {
  const raw = chip.dataset.companyTag;
  if (!raw) return null;
  try {
    return toCompanyTag(JSON.parse(raw) as CompanyTagFields);
  } catch {
    return null;
  }
}

/**
 * Opens the side panel on the tag of a clicked company chip, with no request:
 * the tag is everything the panel shows. The panel's chevrons step across every
 * chip of the conversation, so the entries are read from the DOM in reading
 * order, each chip carrying its tag. A chip whose tag cannot be read is
 * skipped, but still counts in the `anchor` ordinal the chat scrolls back to.
 */
export function openCompanyTagPreview({
  tag,
  key,
  trigger,
  surface,
}: {
  tag: CompanyTag;
  key: string;
  trigger: HTMLElement | null;
  surface: CompanyPreviewPanelSurface;
}): void {
  const options = { modal: false, surface };
  const root = trigger?.closest<HTMLElement>("[data-company-mentions-root]");

  if (root && trigger) {
    const chips = Array.from(
      root.querySelectorAll<HTMLElement>("[data-company-key]"),
    );
    const entries: CompanyPreviewPanelEntry[] = [];
    let clickedIndex = -1;

    chips.forEach((chip, ordinal) => {
      const chipKey = chip.dataset.companyKey;
      const isClicked = chip === trigger;
      const chipTag = isClicked ? tag : tagOfChip(chip);
      if (!chipKey || !chipTag) return;
      entries.push({
        key: chipKey,
        anchor: ordinal,
        company: null,
        tag: chipTag,
      });
      if (isClicked) clickedIndex = entries.length - 1;
    });

    if (clickedIndex >= 0) {
      companyPreviewPanelStore.openAt(entries, clickedIndex, options);
      return;
    }
  }

  companyPreviewPanelStore.openAt(
    [{ key, company: null, tag }],
    0,
    options,
  );
}
